import { useEffect, useRef, useState } from 'react';

const AT_BOTTOM_THRESHOLD = 50;

export function useChatScroll(conversationId: string) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);
  const isAtBottomRef = useRef(true);
  const lastScrollTopRef = useRef(0);

  const jumpToBottom = (element: HTMLDivElement) => {
    element.scrollTo({ top: element.scrollHeight, behavior: 'instant' });
    lastScrollTopRef.current = element.scrollTop;
  };

  // Only moving up leaves the bottom. Message text is rendered by Lexical after React commits, so
  // content can grow between a jump to the bottom and its scroll event, which would otherwise read
  // as the reader having scrolled away.
  const handleScroll = () => {
    if (!scrollElement) return;
    const scrolledUp = scrollElement.scrollTop < lastScrollTopRef.current;
    lastScrollTopRef.current = scrollElement.scrollTop;
    const distanceFromBottom =
      scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
    if (distanceFromBottom <= AT_BOTTOM_THRESHOLD) {
      isAtBottomRef.current = true;
    } else if (scrolledUp) {
      isAtBottomRef.current = false;
    }
  };

  // Expanding a thinking tile or an email draft grows the content, and pinning would push the
  // control the reader just used out of view. Sending a message pins again.
  const handleContentInteraction = () => {
    isAtBottomRef.current = false;
  };

  const forceScrollToBottom = () => {
    isAtBottomRef.current = true;
    if (scrollElement) jumpToBottom(scrollElement);
  };

  useEffect(() => {
    if (!scrollElement) return;
    isAtBottomRef.current = true;
    jumpToBottom(scrollElement);
  }, [scrollElement, conversationId]);

  // Heights settle after any effect keyed on the messages could run (Lexical text, cards fetched
  // separately, collapsibles, the input growing), so follow size changes rather than message changes.
  useEffect(() => {
    if (!scrollElement) return;
    const observer = new ResizeObserver(() => {
      if (isAtBottomRef.current) jumpToBottom(scrollElement);
    });
    observer.observe(scrollElement);
    if (contentElement) observer.observe(contentElement);
    return () => observer.disconnect();
  }, [scrollElement, contentElement]);

  return {
    scrollRef: setScrollElement,
    contentRef: setContentElement,
    handleScroll,
    handleContentInteraction,
    forceScrollToBottom,
  };
}
